/** Flip to true to restore manual Place Anchors UI on the canonical /footwear-studio route. */
export const SHOW_MANUAL_ANCHORS = false;

/**
 * Phase 0 Master Hub: persisted version history, active master selection, pins.
 * Default OFF — set VITE_STRONOM_MASTER_HUB_PHASE0=true to enable.
 */
export const STRONOM_MASTER_HUB_PHASE0 =
  import.meta.env.VITE_STRONOM_MASTER_HUB_PHASE0 === "true" ||
  import.meta.env.VITE_STRONOM_MASTER_HUB_PHASE0 === "1";

/**
 * CC-CHECK-1: check-before-generating takeover in Design Intent (side|refine).
 * Default ON — set VITE_STRONOM_CONTRACT_CHECK=false to switch off (emergency).
 */
export function resolveContractCheckFlag(
  raw: string | undefined,
): boolean {
  return raw !== "false" && raw !== "0";
}

export const CONTRACT_CHECK = resolveContractCheckFlag(
  import.meta.env.VITE_STRONOM_CONTRACT_CHECK as string | undefined,
);

/**
 * Footwear Studio canonical style/collection records (Stage 1.1).
 * Default OFF — set VITE_STRONOM_FS_STYLE_RECORDS=true to enable.
 */
export const STRONOM_FS_STYLE_RECORDS =
  import.meta.env.VITE_STRONOM_FS_STYLE_RECORDS === "true" ||
  import.meta.env.VITE_STRONOM_FS_STYLE_RECORDS === "1";

/**
 * Identity binding: profile bootstrap + org_key minting on session.
 * Default OFF — set VITE_STRONOM_IDENTITY_BINDING=true to enable.
 */
export const STRONOM_IDENTITY_BINDING =
  import.meta.env.VITE_STRONOM_IDENTITY_BINDING === "true" ||
  import.meta.env.VITE_STRONOM_IDENTITY_BINDING === "1";

/**
 * HIST-1b: dual-key version-line comparison (upload_path vs style_id), log-only.
 * Default OFF — set VITE_STRONOM_HIST1_STYLE_KEY=true to enable.
 */
export const STRONOM_HIST1_STYLE_KEY =
  import.meta.env.VITE_STRONOM_HIST1_STYLE_KEY === "true" ||
  import.meta.env.VITE_STRONOM_HIST1_STYLE_KEY === "1";

/**
 * HUB-3a: Styles Hub as shadcn Sheet + client-side filter/sort (vs dropdown).
 * Default OFF — set VITE_STRONOM_HUB_SIDESHEET=true to enable. Dropdown remains fallback.
 */
export const STRONOM_HUB_SIDESHEET =
  import.meta.env.VITE_STRONOM_HUB_SIDESHEET === "true" ||
  import.meta.env.VITE_STRONOM_HUB_SIDESHEET === "1";

/**
 * Core live-phase UI: narrate locking → generating → verifying from mid-job payload.
 * Default OFF — set VITE_STRONOM_CORE_LIVE_PHASE=true to enable.
 */
export const STRONOM_CORE_LIVE_PHASE =
  import.meta.env.VITE_STRONOM_CORE_LIVE_PHASE === "true" ||
  import.meta.env.VITE_STRONOM_CORE_LIVE_PHASE === "1";

/**
 * Collapsible left rail: icons-only by default, expandable to show labels.
 * Default OFF — set VITE_STRONOM_COLLAPSIBLE_RAIL=true to enable.
 */
export const STRONOM_COLLAPSIBLE_RAIL =
  import.meta.env.VITE_STRONOM_COLLAPSIBLE_RAIL === "true" ||
  import.meta.env.VITE_STRONOM_COLLAPSIBLE_RAIL === "1";

/**
 * Full-width All Designs library page at /footwear-studio/library.
 * Default OFF — set VITE_STRONOM_STUDIO_LIBRARY=true to enable.
 */
export const STRONOM_STUDIO_LIBRARY =
  import.meta.env.VITE_STRONOM_STUDIO_LIBRARY === "true" ||
  import.meta.env.VITE_STRONOM_STUDIO_LIBRARY === "1";

export type CorePhase =
  | "idle"
  | "locking"
  | "generating"
  | "verifying"
  | "verified"
  | "flagged"
  | "unchecked";

export type CoreAuditVerdict = {
  ok: boolean | null;
  status: string | null;
  failure_class: string | null;
  /** Optional audit reason string (e.g. angle identity viewpoint miss). */
  reason?: string | null;
  /** Per-property maps from the audit. Values are true (held), false (deviated),
   *  or null when the property could not be assessed. */
  geometry?: Record<string, boolean | null> | null;
  color_material?: Record<string, boolean | null> | null;
  /** Angle identity: expected-viewpoint check. Included in CHECKED chips. */
  viewpoint?: Record<string, boolean | null> | null;
  /**
   * Layer 1: per allow* flag → whether the requested change was applied.
   * false must never count as "held" — surfaces as FLAGGED / change-not-applied.
   */
  applied?: Record<string, boolean | null> | null;
};

/** Live Core thread panel — populated from jobs.payload.prompt_debug (flag ON only). */
export type CoreThreadData = {
  intentSummary: string | null;
  generationProfile: string | null;
  referenceCategory: string | null;
  referenceHeelClass: string | null;
  referenceView: string | null;
  hasReferenceFacts: boolean;
  explicitChanges: Record<string, boolean> | null;
};

/** Metadata for the Core record panel (flag ON only). */
export type CoreJobMeta = {
  jobId?: string | null;
  pipeline?: string | null;
  model?: string | null;
  profile?: string | null;
  contractHash?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
};
